import { Avatar, Box, ButtonBase, Typography } from "@mui/material";
import { useContext } from "react";
import { AppContext } from "../../context/AppContext";
import { INK, INK_2, LINE, MUTED, cardSx, initials, isValidURL } from "./shared";

function useAgent() {
	const { appState } = useContext(AppContext);
	const { info, colorPallete } = appState;
	return { agent: info.agent, client: info.client, brand: colorPallete["scheme1"] };
}

function AgentAvatar(props: { size: number }) {
	const { agent, brand } = useAgent();
	return (
		<Avatar
			src={agent.agent_photo || undefined}
			alt=""
			sx={{ width: props.size, height: props.size, bgcolor: brand, fontSize: props.size * 0.34, fontWeight: 700 }}
		>
			{initials(agent.agentName)}
		</Avatar>
	);
}

const primarySx = (brand: string) => ({
	justifyContent: "center",
	color: "#fff",
	backgroundColor: brand,
	borderRadius: "10px",
	px: "14px",
	py: "9px",
	fontSize: "13px",
	fontWeight: 700,
	whiteSpace: "nowrap" as const,
	"&:hover": { filter: "brightness(.94)" },
});
const secondarySx = {
	justifyContent: "center",
	color: INK,
	border: "1px solid #d9dee6",
	borderRadius: "10px",
	px: "12px",
	py: "8px",
	fontSize: "13px",
	fontWeight: 700,
	whiteSpace: "nowrap" as const,
	"&:hover": { backgroundColor: "#f5f7fa" },
};

// Book a call when the agent has a booking link, otherwise a phone call.
function PrimaryAction() {
	const { agent, brand } = useAgent();
	if (isValidURL(agent.scheduleLink)) {
		return (
			<ButtonBase component="a" href={agent.scheduleLink} target="_blank" rel="noreferrer" sx={primarySx(brand)}>
				Book a call
			</ButtonBase>
		);
	}
	if (agent.agent_phone_no) {
		return (
			<ButtonBase component="a" href={`tel:${agent.agent_phone_no}`} sx={primarySx(brand)}>
				Call me
			</ButtonBase>
		);
	}
	return null;
}

function TextAction() {
	const { agent } = useAgent();
	if (!agent.agent_phone_no) return null;
	return (
		<ButtonBase component="a" href={`sms:${agent.agent_phone_no}`} sx={secondarySx}>
			Text me
		</ButtonBase>
	);
}

// The agent's greeting to the homeowner.
export function AgentNote(props: { withAvatar?: boolean }) {
	const { agent, client } = useAgent();
	if (!agent.messageToClient) return null;
	const name = client?.name?.trim();
	return (
		<Box sx={{ display: "grid", gridTemplateColumns: props.withAvatar ? "40px 1fr" : "1fr", gap: "12px" }}>
			{props.withAvatar && <AgentAvatar size={40} />}
			<Box sx={{ minWidth: 0 }}>
				{name && (
					<Typography sx={{ fontWeight: 700, fontSize: "14px", color: INK, mb: "4px" }}>
						Hi {name},
					</Typography>
				)}
				<Typography sx={{ fontSize: "14px", lineHeight: 1.55, color: INK_2, whiteSpace: "pre-line" }}>
					{agent.messageToClient}
				</Typography>
				{agent.signature && (
					<Typography sx={{ fontWeight: 700, fontSize: "13px", color: INK, mt: "8px" }}>
						{agent.signature}
					</Typography>
				)}
			</Box>
		</Box>
	);
}

// Desktop sidebar card: who the agent is, their note, and how to reach them.
export function AgentCard() {
	const { agent } = useAgent();
	const subtitle = [agent.businessTitle, agent.businessName].filter(Boolean).join(", ");
	return (
		<Box sx={{ ...cardSx, p: "22px", display: "flex", flexDirection: "column", gap: "16px" }}>
			<Box sx={{ display: "flex", gap: "14px", alignItems: "center" }}>
				<AgentAvatar size={56} />
				<Box sx={{ minWidth: 0 }}>
					<Typography sx={{ fontWeight: 800, fontSize: "16px", color: INK }}>{agent.agentName}</Typography>
					{subtitle && <Typography sx={{ fontSize: "13px", color: MUTED }}>{subtitle}</Typography>}
				</Box>
			</Box>
			<AgentNote />
			{(agent.agent_phone_no || agent.email) && (
				<Box
					sx={{
						display: "grid",
						gap: "6px",
						py: "12px",
						borderTop: `1px solid ${LINE}`,
						borderBottom: `1px solid ${LINE}`,
						fontSize: "13px",
					}}
				>
					{agent.agent_phone_no && (
						<Box sx={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
							<Box component="span" sx={{ color: MUTED }}>
								Phone
							</Box>
							<Box
								component="a"
								href={`tel:${agent.agent_phone_no}`}
								sx={{ color: INK, textDecoration: "none" }}
							>
								{agent.agent_phone_no}
							</Box>
						</Box>
					)}
					{agent.email && (
						<Box sx={{ display: "flex", justifyContent: "space-between", gap: "12px", minWidth: 0 }}>
							<Box component="span" sx={{ color: MUTED }}>
								Email
							</Box>
							<Box
								component="a"
								href={`mailto:${agent.email}`}
								sx={{
									color: INK,
									textDecoration: "none",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
							>
								{agent.email}
							</Box>
						</Box>
					)}
				</Box>
			)}
			<Box sx={{ display: "flex", gap: "8px", "& > *": { flex: 1 } }}>
				<TextAction />
				<PrimaryAction />
			</Box>
		</Box>
	);
}

// Phone only: a slim bar pinned to the bottom so the agent is always one tap away.
export function AgentDock() {
	const { agent } = useAgent();
	return (
		<Box
			sx={{
				display: { xs: "flex", md: "none" },
				position: "fixed",
				left: 0,
				right: 0,
				bottom: 0,
				zIndex: 1000,
				alignItems: "center",
				gap: "10px",
				px: "14px",
				pt: "10px",
				pb: "calc(12px + env(safe-area-inset-bottom, 0px))",
				backgroundColor: "rgba(255,255,255,.95)",
				backdropFilter: "blur(8px)",
				borderTop: `1px solid ${LINE}`,
			}}
		>
			<AgentAvatar size={36} />
			<Box sx={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
				<Typography noWrap sx={{ fontWeight: 700, fontSize: "13px", color: INK }}>
					{agent.agentName}
				</Typography>
				{agent.agent_phone_no && (
					<Typography noWrap sx={{ fontSize: "11px", color: MUTED }}>
						{agent.agent_phone_no}
					</Typography>
				)}
			</Box>
			<TextAction />
			<PrimaryAction />
		</Box>
	);
}
